/**
 * Synchronous, deterministic project and package ZIPs, backed by fflate.
 * Entries stay stored (no compression), in caller order, with a fixed DOS
 * timestamp. ZIP metadata is library-owned; hashes of newly built archives
 * may differ from archives produced by the previous writer.
 */
import { Zip, ZipPassThrough, unzipSync } from 'fflate';
import CRC32 from 'crc-32';
export const PACKAGE_ARCHIVE_EXTENSION = '.aspkg';
export function writePackageArchive(entries) {
    const seen = new Set();
    const chunks = [];
    let length = 0;
    const zip = new Zip((error, chunk) => {
        if (error)
            throw error;
        chunks.push(chunk);
        length += chunk.length;
    });
    for (const entry of entries) {
        if (seen.has(entry.path)) {
            throw new Error(`Cannot build an archive containing "${entry.path}" twice.`);
        }
        seen.add(entry.path);
        const file = new ZipPassThrough(entry.path);
        // fflate encodes local calendar fields, so use local midnight in every TZ.
        file.mtime = new Date(1980, 0, 1);
        file.attrs = 0o100644 << 16;
        zip.add(file);
        file.push(entry.bytes, true);
    }
    zip.end();
    const result = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) {
        result.set(chunk, offset);
        offset += chunk.length;
    }
    return result;
}
/** Stored-only policy is kept for synchronous project/package consumers. */
export function readPackageArchive(bytes) {
    checkDirectoryHeaders(bytes);
    const directory = [];
    const seen = new Set();
    try {
        const files = unzipSync(bytes, {
            filter: (file) => {
                if (file.compression !== 0) {
                    throw new Error(`Archive entry "${file.name}" uses compression method ${file.compression}; this reader implements stored entries only.`);
                }
                if (seen.has(file.name))
                    throw new Error(`Duplicate archive entry "${file.name}".`);
                seen.add(file.name);
                directory.push(file);
                return true;
            }
        });
        return directory.map((file) => {
            const data = files[file.name];
            if (!data || data.length !== file.originalSize || file.size !== file.originalSize) {
                throw new Error(`Archive entry "${file.name}" has a malformed size.`);
            }
            return { path: file.name, bytes: data };
        });
    }
    catch (error) {
        throw new Error(`Not a ZIP archive or malformed archive: ${error instanceof Error ? error.message : String(error)}`, { cause: error });
    }
}
/**
 * fflate tolerates damaged central-header signatures. Retain the old reader's
 * refusal of malformed directories; decoding names and payloads stays in fflate.
 */
function checkDirectoryHeaders(bytes) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let end = bytes.length - 22;
    const earliest = Math.max(0, bytes.length - 22 - 65535);
    while (end >= earliest && view.getUint32(end, true) !== 0x06054b50)
        end -= 1;
    if (end < earliest)
        throw new Error('Not a ZIP archive: no end-of-central-directory record.');
    const count = view.getUint16(end + 10, true);
    let at = view.getUint32(end + 16, true);
    for (let index = 0; index < count; index += 1) {
        if (at + 46 > end || view.getUint32(at, true) !== 0x02014b50) {
            throw new Error(`Archive central directory entry ${index} is malformed.`);
        }
        at +=
            46 +
                view.getUint16(at + 28, true) +
                view.getUint16(at + 30, true) +
                view.getUint16(at + 32, true);
        if (at > end)
            throw new Error(`Archive central directory entry ${index} is malformed.`);
    }
}
/** Compatibility export; the checksum implementation is owned by crc-32. */
export function crc32(bytes) {
    return CRC32.buf(bytes) >>> 0;
}
/**
 * SHA-256 of an archive, lower-case hex — the identity a repository publishes and
 * an installer verifies.
 *
 * Uses Web Crypto, which is a standard global in browsers and in Node, rather
 * than a hand-written digest or a dependency: a hash function is exactly the
 * kind of code that must not be reimplemented here.
 *
 * @throws {Error} when the environment exposes no Web Crypto implementation.
 */
export async function hashArchive(bytes) {
    const subtle = globalThis.crypto?.subtle;
    if (subtle === undefined) {
        throw new Error('Cannot hash a package archive: this environment exposes no Web Crypto (globalThis.crypto.subtle).');
    }
    // Copied into a fresh view: Web Crypto's `BufferSource` excludes the
    // possibly-shared buffer a caller's `Uint8Array` may be backed by.
    const digest = await subtle.digest('SHA-256', new Uint8Array(bytes));
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
//# sourceMappingURL=archive.js.map